import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import moment from 'moment'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import TextButton from 'LuxKit/Button/TextButton'
import MultipleRoomsBookingOption from './MultipleRoomsBookingOption'
import useToggle from 'hooks/useToggle'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import Caption from 'LuxKit/Typography/Caption'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import MultipleRoomsBookingRoomsModal from './MultipleRoomsBookingRoomsModal'
import MultipleRoomsBookingContentFooter from './MultipleRoomsBookingContentFooter'
import { useInView } from 'react-intersection-observer'
import {
  FlightsInfoByRoom, getActivePackagesByRoom, getAvailablePackagesByRoom, getCalendarsToBeFetchedByRoom, getCheapestPackagesByRoom, getDurationPackageOptionsByRoom, getFlightsInfoByRoom, isLoadingByRoom, LoadingStatusByRoom,
  PackageOptionViewByRoom,
  PackagesOptionsViewsByRoom,
} from 'selectors/offerPage/multipleRoomBookingSelectors'
import { useAppDispatch } from 'hooks/reduxHooks'
import BnblModal from '../BnblModal/BnblModal'
import { initialiseBnblOfferModal } from 'actions/BnblModalActions'
import { countBy, EmptyArray, groupBy, nonNullable } from 'lib/array/arrayUtils'
import { getBnblPackageViews, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { getBnblPackageViewByNumberOfRooms } from '../BnblModal/utils'
import { arrayToStringListFormat } from 'lib/string/stringUtils'
import RequestConnectingRoomsInput from './RequestConnectingRoomsInput'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import TextLink from 'LuxKit/TextLink'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import PriceStackLuxPlusCompactPrice from 'LuxKit/PricePoints/LuxPlus/PriceStackLuxPlusCompactPrice'
import config from 'constants/config'
import Pane from 'components/Common/Pane'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import MultiRoomOfferBookButton from './MultiRoomOfferBookButton'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { mergeObjects } from 'lib/object/objectUtils'
import { selectLoggedIn } from 'selectors/accountSelectors'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import GeoContext from 'contexts/geoContext'
import ContactContext from 'contexts/contactContext'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import useHasGotToAnchor from 'hooks/useHasGotToAnchor'
import clsx from 'clsx'
import noop from 'lib/function/noop'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import Group from 'components/utils/Group'
import MultiRoomOfferAddToCartButton from './MultiRoomOfferAddToCartButton'
import MultiRoomOfferSelectRoomsButton from './MultiRoomOfferSelectRoomsButton'
import Divider from 'LuxKit/Divider'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'

const PackageOptionsContainer = styled(LayoutContainer)`
  padding: 0;
  position: relative;
`

const MobilePaddingGroup = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding: 0;
  }
`

const RoomPackageHeader = styled(Group)`
  padding: ${rem(16)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const NoPackagesContainer = styled(Group)`
  padding: ${rem(16)};
`

const GreyGroup = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.six};

  ${mediaQueryUp.tablet} {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }

  &.with-banner {
    flex-direction: column;
    margin-top: ${rem(8)};
    gap: ${rem(8)};

    ${mediaQueryUp.desktop} {
      flex-direction: row;
      margin-top: 0;
      align-self: flex-end;
      padding: 0;
    }
  }
`

interface FeedbackBannerMessage {
  error: {
    title: string;
    message?: string;
  } | null;
}

interface Props {
  rooms?: Array<App.RoomOccupants>;
  offer: App.HotelOffer;
  duration?: number;
  activeFilter?: OfferPageFilterType;
  cheapestPackagesByRoom: PackageOptionViewByRoom;
  activePackagesByRoom: PackageOptionViewByRoom;
  isLoadingByRoom: LoadingStatusByRoom;
  calendarsToBeFetchedByRoom: LoadingStatusByRoom;
  flightsInfoByRoom: FlightsInfoByRoom;
  availablePackagesByRoom: PackagesOptionsViewsByRoom;
  durationPackagesByRoom: PackagesOptionsViewsByRoom;
  account?: App.AuthAccount;
  loggedIn?: boolean;
  baseBnblPackageViews: Array<OfferPackageOptionView>;
  roomTypesByRoom: { [roomId: string]: string };
  isMultiRoomConnected: boolean;
  canRedeemLuxPlusBenefits: boolean;
  userMultiItemCartEnabled: boolean;
  onStickyFooterToggle?: (visible: boolean) => void;
  checkIn?: string;
}

function MultipleRoomsBookingContent(props: Props) {
  const {
    rooms = EmptyArray as Array<App.RoomOccupants>,
    offer,
    duration,
    activeFilter,
    cheapestPackagesByRoom,
    activePackagesByRoom,
    isLoadingByRoom,
    calendarsToBeFetchedByRoom,
    flightsInfoByRoom,
    availablePackagesByRoom,
    durationPackagesByRoom,
    loggedIn: isLoggedIn,
    baseBnblPackageViews,
    roomTypesByRoom,
    isMultiRoomConnected,
    canRedeemLuxPlusBenefits,
    userMultiItemCartEnabled,
    onStickyFooterToggle = noop,
    checkIn,
  } = props

  const showModal = useContext(ModalContext)
  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()

  const [reviewPageRef, reviewPageInView] = useInView()
  const [footerRef, footerInView] = useInView({ rootMargin: '0px 0px -100px 0px' })
  const { currentRegionCode } = useContext(GeoContext)
  const currentContact = useContext(ContactContext)

  const isFlightsForAllRoomsSelected = useMemo(() => Object.values(flightsInfoByRoom).every(flightsInfo => flightsInfo.isFlightsSelected), [flightsInfoByRoom])

  const [buyNowBookLater, _, openBNBLModal, closeBNBLModal] = useToggle()

  const hasNoFlightPrices = useMemo(() => !!Object.values(flightsInfoByRoom).find(pkg => !pkg.flightPrice), [flightsInfoByRoom])

  const isMobileDevice = useIsMobileDevice()

  const lastValidPackagesIdsByRoom = useRef<{ [roomId: string]: string | undefined }>({})
  const previousPackagesByRoom = useRef<PackageOptionViewByRoom>({})
  const previousDuration = useRef<number | null>(null)
  const selectionAmountByPackage = useRef<Map<string, number>>(new Map<string, number>())

  const packagesByRoom = useMemo(() => {
    const selectedPackages = { ...activePackagesByRoom }
    Object
      .entries(selectedPackages)
      // filters only rooms without package
      .filter(([, selectedPackage]) => !selectedPackage)
      .forEach(([roomId]) => {
        const isThisRoomLoading = isLoadingByRoom[roomId] || calendarsToBeFetchedByRoom[roomId]
        // If this room has a "loading" state, we select
        // the first available package for it, only for the
        // component to show the loading indicator
        selectedPackages[roomId] = isThisRoomLoading ? durationPackagesByRoom[roomId][0] : undefined
      })

    return selectedPackages
  }, [activePackagesByRoom, isLoadingByRoom, durationPackagesByRoom, calendarsToBeFetchedByRoom])

  const isCheapestPackagesActiveByRoom = useMemo(() => {
    const cheapestByRoom: Record<string, boolean> = {}
    Object
      .entries(packagesByRoom)
      .forEach(([roomId, packageOption]) => {
        cheapestByRoom[roomId] = !!(packageOption &&
          cheapestPackagesByRoom[roomId] &&
          cheapestPackagesByRoom[roomId].package.uniqueKey === packageOption.package.uniqueKey)
      })

    return cheapestByRoom
  }, [packagesByRoom, cheapestPackagesByRoom])

  const onOpenRoomsModal = useCallback(async(changedRoom: App.RoomOccupants) => {
    const changedRoomIndex = rooms.findIndex(room => room.roomId === changedRoom.roomId)

    const packageOption = await showModal<App.HotelPackage | undefined>(
      <MultipleRoomsBookingRoomsModal
        room={changedRoom}
        title={`Upgrade package for room ${changedRoomIndex + 1} of ${rooms.length}`}
        subtitle={`${availablePackagesByRoom[changedRoom.roomId]?.length || 0} packages available`}
        availablePackages={availablePackagesByRoom[changedRoom.roomId] || []}
        selectedPackageOption={packagesByRoom[changedRoom.roomId]}
        isFlightsSelected={!!flightsInfoByRoom[changedRoom.roomId]?.isFlightsSelected}
        isFlightsEnabled={!buyNowBookLater && flightsInfoByRoom[changedRoom.roomId]?.isFlightsAvailable}
      />,
    )

    if (packageOption) {
      offerPageDispatch({
        type: OfferPageActions.selectRoomPackage,
        packageOption,
        room: changedRoom,
      })
    }
  }, [availablePackagesByRoom, buyNowBookLater, flightsInfoByRoom, offerPageDispatch, packagesByRoom, rooms, showModal])

  const {
    totalRoomsPrice,
    totalRoomsBaseMemberPrice,
    totalRoomsMemberPrice,
    totalRoomsFlightPrice,
  } = useMemo(() => (
    Object.keys(packagesByRoom).reduce((acc, roomId) => {
      if (!packagesByRoom[roomId]) return acc

      const {
        hotelPrice,
        hotelMemberPrice,
        baseMemberPrice,
        flightPrice,
        propertyFees = 0,
      } = packagesByRoom[roomId]

      const { isFlightsSelected } = flightsInfoByRoom[roomId]

      const totalHotelPrice = hotelPrice + propertyFees
      const totalHotelMemberPrice = hotelMemberPrice + propertyFees
      const totalRoomPrice = isFlightsSelected ? totalHotelPrice + flightPrice : totalHotelPrice
      const totalRoomMemberPrice = isFlightsSelected ? totalHotelMemberPrice + flightPrice : totalHotelMemberPrice
      const totalFlightPrice = isFlightsSelected ? flightPrice : 0

      return {
        totalRoomsPrice: acc.totalRoomsPrice + totalRoomPrice,
        totalRoomsMemberPrice: acc.totalRoomsMemberPrice + (totalRoomMemberPrice || totalRoomPrice),
        totalRoomsBaseMemberPrice: acc.totalRoomsBaseMemberPrice + baseMemberPrice,
        totalRoomsFlightPrice: acc.totalRoomsFlightPrice + totalFlightPrice,
      }
    }, { totalRoomsPrice: 0, totalRoomsMemberPrice: 0, totalRoomsBaseMemberPrice: 0, totalRoomsFlightPrice: 0 })

  ), [packagesByRoom, flightsInfoByRoom])

  const isBookNowBtnDisabled = useMemo(() => (
    Object.values(isLoadingByRoom).some(Boolean) ||
    Object.values(calendarsToBeFetchedByRoom).some(Boolean) ||
    Object.values(packagesByRoom).some(pkg => !pkg)
  ), [isLoadingByRoom, calendarsToBeFetchedByRoom, packagesByRoom])

  const onOpenBnblModal = useCallback(async(e: React.MouseEvent<HTMLAnchorElement>) => {
    const openAndInitaliseBnblModal = () => {
      e.preventDefault()
      openBNBLModal()

      offerPageDispatch({
        type: OfferPageActions.toggleFlightsPrice,
        isFlightsSelected: false,
      })

      const selectedPackages = nonNullable(Object.values(packagesByRoom))
      const groupedPackagesRecords = groupBy(selectedPackages, pkg => pkg.package.uniqueKey)
      const packages = [...groupedPackagesRecords.entries()].map(groupedPackage => {
        const basePackage = baseBnblPackageViews.find(packageView => packageView.package.uniqueKey === groupedPackage[0])!
        const numberOfRooms = groupedPackage[1].length

        return {
          packageView: getBnblPackageViewByNumberOfRooms(basePackage, numberOfRooms),
          numberOfRooms,
        }
      })

      dispatch(initialiseBnblOfferModal(packages, offer.id, false))
    }

    if (!isLoggedIn) {
      e.preventDefault()

      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (result.loggedIn) {
        openAndInitaliseBnblModal()
      }
    }

    if (isLoggedIn) {
      openAndInitaliseBnblModal()
    }
  }, [isLoggedIn, openBNBLModal, offerPageDispatch, packagesByRoom, dispatch, offer.id, baseBnblPackageViews, showModal])

  const onRemoveRoom = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    const index = Number(e.currentTarget.dataset.idx)
    const newRooms = rooms?.filter((room, idx) => idx !== index)

    offerPageDispatch({
      type: OfferPageActions.setRooms,
      rooms: newRooms,
    })
  }, [rooms, offerPageDispatch])

  const feedbackMessage = useMemo(() => {
    return ({
      error: (title: string, message: string = '') => showSnackbar(message, 'critical', { heading: title }),
      success: (title: string, message: string = '') => showSnackbar(message, 'success', { heading: title }),
    })
  }, [])

  const getChangedRoomsFeedbackMessage = useCallback(() => {
    const resultMessage: FeedbackBannerMessage = {
      error: null,
    }

    const changedRoomsIds =
      Object
        .keys(packagesByRoom)
        .filter(roomId => roomId in previousPackagesByRoom.current && packagesByRoom[roomId]?.package.uniqueKey !== previousPackagesByRoom.current[roomId]?.package.uniqueKey)

    if (changedRoomsIds.length) {
      const roomsIdsWithoutPackage = changedRoomsIds.filter(roomId => !packagesByRoom[roomId])
      const roomsIdsWithPackage = changedRoomsIds.filter(roomId => packagesByRoom[roomId])

      if (roomsIdsWithoutPackage.length) {
        const roomsToString = roomsIdsWithoutPackage.map(roomId => `Room ${rooms.findIndex(room => room.roomId === roomId) + 1}`)
        resultMessage.error = {
          title: `No available packages for ${arrayToStringListFormat(roomsToString, 'and')}`,
          message: 'Please revise number of guests or adjust selected dates.',
        }
      } else {
        const didDurationChanged = previousDuration.current != duration
        const roomsWithDifferentRoomType = roomsIdsWithPackage.filter(roomId => roomTypesByRoom[roomId] && roomTypesByRoom[roomId] != packagesByRoom[roomId]?.package.roomType?.id)

        if (didDurationChanged && roomsWithDifferentRoomType.length) {
          const roomsToString = roomsWithDifferentRoomType.map(roomId => `Room ${rooms.findIndex(room => room.roomId === roomId) + 1}`)
          resultMessage.error = {
            title: `The room type for ${arrayToStringListFormat(roomsToString, 'and')} is unavailable for selected duration`,
            message: 'We have selected the lowest price package available',
          }
        }
      }
    }

    return resultMessage
  }, [packagesByRoom, previousPackagesByRoom, rooms, previousDuration, duration, roomTypesByRoom])

  const getNewRoomsFeedbackMessage = useCallback(() => {
    const resultMessage: FeedbackBannerMessage = {
      error: null,
    }

    const previousRoomsIds = new Set(Object.keys(previousPackagesByRoom.current))
    const currentRoomsIds = Object.keys(packagesByRoom)
    if (previousRoomsIds.size < currentRoomsIds.length) {
      const newRoomsIds = currentRoomsIds.filter(roomId => !previousRoomsIds.has(roomId))
      const roomsIdsWithoutPackage = newRoomsIds.filter(roomId => !packagesByRoom[roomId])

      if (roomsIdsWithoutPackage.length) {
        const roomsIndexes = roomsIdsWithoutPackage.map(roomId => `Room ${rooms.findIndex(room => room.roomId === roomId) + 1}`)
        resultMessage.error = {
          title: `No available packages for ${arrayToStringListFormat(roomsIndexes, 'and')}`,
          message: 'Please revise number of guests or adjust selected dates.',
        }
      }
    }

    return resultMessage
  }, [packagesByRoom, previousPackagesByRoom, rooms])

  useEffect(() => {
    const isCalendarsLoading = Object.values(isLoadingByRoom).some(Boolean) || Object.values(calendarsToBeFetchedByRoom).some(Boolean)
    if (isCalendarsLoading || activeFilter) return

    const changedRoomsFeedbackMessage = getChangedRoomsFeedbackMessage()
    const newRoomsFeedbackMessage = getNewRoomsFeedbackMessage()

    if (changedRoomsFeedbackMessage.error) {
      feedbackMessage.error(
        changedRoomsFeedbackMessage.error.title,
        changedRoomsFeedbackMessage.error.message,
      )
    } else if (newRoomsFeedbackMessage.error) {
      feedbackMessage.error(
        newRoomsFeedbackMessage.error.title,
        newRoomsFeedbackMessage.error.message,
      )
    }

    Object.keys(packagesByRoom).forEach(roomId => {
      if (!(roomId in lastValidPackagesIdsByRoom.current) || packagesByRoom[roomId]) {
        lastValidPackagesIdsByRoom.current[roomId] = packagesByRoom[roomId]?.package.uniqueKey
      }
    })

    previousPackagesByRoom.current = packagesByRoom
    previousDuration.current = duration ?? null
    selectionAmountByPackage.current = countBy(packagesByRoom, (pkg) => pkg?.package.uniqueKey ?? '')
  }, [
    isLoadingByRoom, calendarsToBeFetchedByRoom, packagesByRoom, previousPackagesByRoom, feedbackMessage,
    activeFilter, duration, getNewRoomsFeedbackMessage, getChangedRoomsFeedbackMessage,
  ])

  const openOccupancyPicker = useCallback(() => {
    offerPageDispatch({
      type: OfferPageActions.toggleFilter,
      filter: 'capacity',
    })
  }, [offerPageDispatch])

  const onToggleConnectedRooms = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleConnectedRooms })
  }, [offerPageDispatch])

  const shouldPreventMultipleRoomsBooking = rooms.length > 2 && canRedeemLuxPlusBenefits && totalRoomsBaseMemberPrice > 0

  const packageStartRef = useRef<HTMLDivElement>(null)
  const reachedAnchor = useHasGotToAnchor(packageStartRef?.current, -500, 25)

  useEffect(() => {
    onStickyFooterToggle(!reachedAnchor)
  }, [onStickyFooterToggle, reachedAnchor])

  const loyaltyHotelOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return Object.values(packagesByRoom)?.map(packageView => (
      generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView })
    ))
  }, [offer, packagesByRoom])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    ...loyaltyHotelOptions,
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      flightPrice: totalRoomsFlightPrice,
      skip: !isFlightsForAllRoomsSelected,
    }),
  ], [offer, isFlightsForAllRoomsSelected, totalRoomsFlightPrice, loyaltyHotelOptions])

  return (
    <Pane ref={packageStartRef}>
      <PackageOptionsContainer ref={reviewPageRef}>
        <CSSBreakpoint max="mobile">
          <Divider kind="primary" />
        </CSSBreakpoint>
        <GreyGroup direction="vertical" gap={8} tabletGap={24}>
          <MobilePaddingGroup direction="vertical" tabletDirection="horizontal" gap={20} tabletHorizontalAlign="space-between">
            <Heading variant="heading3">Room options</Heading>
          </MobilePaddingGroup>
          {rooms.map((room, index) => {
            const packageOptionView = packagesByRoom[room.roomId]
            return (
              <Pane key={index} border="neutral-five" borderRadius="S">
                <RoomPackageHeader direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={16}>
                  <Group direction="vertical" gap={4} horizontalAlign="start">
                    <Group direction="horizontal" verticalAlign="center" gap={8}>
                      <BodyText variant="large" weight="bold">Room {index + 1} of {rooms.length}</BodyText>
                      <BodyText variant="medium" colour="neutral-two">({formatOccupantsShort(room)})</BodyText>
                    </Group>
                    <TextLink size="medium" weight="regular" data-idx={index} onClick={onRemoveRoom}>
                      Remove
                    </TextLink>
                  </Group>
                  {isCheapestPackagesActiveByRoom[room.roomId] && <StatusLabel variant="confirmation">Best price selected</StatusLabel>}
                </RoomPackageHeader>
                {!packageOptionView && (
                  <NoPackagesContainer direction="vertical" gap={8}>
                    <BodyText variant="large" weight="bold">No packages available</BodyText>
                    {lastValidPackagesIdsByRoom.current[room.roomId] && (
                      <BodyText variant="medium">
                        <TextLink weight="regular" onClick={openOccupancyPicker}>Add another room</TextLink>{' '}
                        to accommodate your number of guests, or{' '}
                        <TextLink weight="regular" onClick={openOccupancyPicker}>Revise number of guests</TextLink>
                      </BodyText>
                    )}
                    {!lastValidPackagesIdsByRoom.current[room.roomId] && (
                      <TextLink weight="regular" onClick={openOccupancyPicker}>Please revise number of guests</TextLink>
                    )}
                  </NoPackagesContainer>
                )}
                {packageOptionView && (
                  <MultipleRoomsBookingOption
                    key={index}
                    offer={offer}
                    room={room}
                    isFlightsEnabled={!buyNowBookLater && flightsInfoByRoom[room.roomId]?.isFlightsAvailable}
                    packageOption={packageOptionView}
                    packageOptionSelectionsAmount={selectionAmountByPackage.current.get(packageOptionView.package.uniqueKey)}
                    isFetching={isLoadingByRoom[room.roomId] || calendarsToBeFetchedByRoom[room.roomId]}
                    isFlightsSelected={flightsInfoByRoom[room.roomId]?.isFlightsSelected}
                    buyNowBookLater={buyNowBookLater}
                    openRoomsModal={onOpenRoomsModal}
                    isUpgradable={config.PACKAGE_CHANGE_UPGRADE_ENABLED && availablePackagesByRoom[room.roomId]?.length > 1}
                    availablePackagesQuantity={availablePackagesByRoom[room.roomId]?.length}
                  />
                )}
              </Pane>
            )
          })}

          <RequestConnectingRoomsInput
            checked={isMultiRoomConnected}
            onToggleConnectRooms={onToggleConnectedRooms}
          />

          <MobilePaddingGroup direction="vertical" gap={20}>
            <Group direction="horizontal" tabletHorizontalAlign="end">
              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={(!checkIn || isFlightsForAllRoomsSelected) ? 'estimate' : 'exact'}
              />
            </Group>

            <MultipleRoomsBookingContentFooter
              ref={footerRef}
              sticky={!isMobileDevice && !footerInView && reviewPageInView}
            >
              <Wrapper className={clsx({ 'with-banner': shouldPreventMultipleRoomsBooking })}>
                {offer.hasBuyNowBookLater && !shouldPreventMultipleRoomsBooking && (
                  <CSSBreakpoint min="desktop">
                    <TextButton kind="tertiary" size="large" onClick={onOpenBnblModal} horizontalOutdent="start">
                      Buy now choose dates later
                    </TextButton>
                    <Caption variant="medium" as="span" wrap="no-wrap">
                      Travel until {moment(offer.travelToDate).format(DMY_CASUAL_FORMAT)}
                    </Caption>
                  </CSSBreakpoint>
                )}

                <Group direction="horizontal" horizontalAlign="space-between" tabletHorizontalAlign="end" verticalAlign="center" gap={24}>
                  <LuxPlusPriceGuard
                    showMemberPrice={totalRoomsBaseMemberPrice > 0}
                    fallbackPrice={
                      <div>
                        <PriceRowPriceCaption>
                          {rooms.length} rooms {hasNoFlightPrices ? 'from' : ''}
                        </PriceRowPriceCaption>
                        <PriceRowPrice
                          size="S"
                          price={totalRoomsPrice}
                          saleUnit="total"
                        />
                      </div>
}
                  >
                    <PriceStackLuxPlusCompactPrice
                      size="M"
                      memberPrice={totalRoomsMemberPrice}
                      saleUnit="total"
                      nonMemberPrice={totalRoomsPrice}
                      horizontalAlign="start"
                    />
                  </LuxPlusPriceGuard>
                  {!shouldPreventMultipleRoomsBooking && <Group direction="vertical-reverse" tabletDirection="horizontal" gap={8}>
                    {userMultiItemCartEnabled && !isFlightsForAllRoomsSelected && <MultiRoomOfferAddToCartButton disabled={isBookNowBtnDisabled} fit="flex" size="large" />}
                    {userMultiItemCartEnabled && isFlightsForAllRoomsSelected && <MultiRoomOfferSelectRoomsButton disabled={isBookNowBtnDisabled} fit="flex" size="large" />}
                    {(!userMultiItemCartEnabled || (userMultiItemCartEnabled && !isFlightsForAllRoomsSelected)) && <MultiRoomOfferBookButton disabled={isBookNowBtnDisabled} size="large" />}
                  </Group>}
                </Group>

                {offer.hasBuyNowBookLater && footerInView && !shouldPreventMultipleRoomsBooking && (
                  <CSSBreakpoint max="tablet">
                    <Group direction="horizontal" gap={8} verticalAlign="center">
                      <TextButton kind="tertiary" size="large" onClick={onOpenBnblModal} horizontalOutdent="start">
                        Buy now choose dates later
                      </TextButton>
                      <Caption variant="medium" as="span" wrap="no-wrap">
                        Travel until {moment(offer.travelToDate).format(DMY_CASUAL_FORMAT)}
                      </Caption>
                    </Group>
                  </CSSBreakpoint>
                )}
                {shouldPreventMultipleRoomsBooking && <MessageBanner
                  kind="informational-primary"
                  heading="Ready to book? Call us to complete your booking"
                  description={`Our team are available ${NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) ? '7 days a week from 9am to 7pm' : '24 hours, 7 days'}`}
                  primaryActionButtonProps={{
                    startIcon: <LinePhoneIcon />,
                    children: `Call ${currentContact.defaultContact.humanReadable}`,
                    href: `tel:${currentContact.defaultContact.number}`,
                  }}
                />}
              </Wrapper>
            </MultipleRoomsBookingContentFooter>
          </MobilePaddingGroup>

          {buyNowBookLater && <BnblModal
            closeBnblModal={closeBNBLModal}
            isOpen={buyNowBookLater}
            offer={offer}
          />}
        </GreyGroup>
      </PackageOptionsContainer>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  duration: state.duration,
  rooms: state.rooms,
  offer: state.offer,
  isMultiRoomConnected: state.isMultiRoomConnected,
  activeFilter: state.activeFilter,
  account: appState.auth.account,
  cheapestPackagesByRoom: getCheapestPackagesByRoom(state, appState),
  activePackagesByRoom: getActivePackagesByRoom(state, appState),
  isLoadingByRoom: isLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  flightsInfoByRoom: getFlightsInfoByRoom(state, appState),
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
  durationPackagesByRoom: getDurationPackageOptionsByRoom(state, appState),
  loggedIn: selectLoggedIn(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
  roomTypesByRoom: mergeObjects(state.multipleRoomPackages.map(mrpkg => ({ [mrpkg.roomId]: mrpkg.roomTypeId }))),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
  userMultiItemCartEnabled: isMultiCartEnabled(appState),
  checkIn: state.checkInDate,
}), MultipleRoomsBookingContent)
